import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Organization, Label, User, PayeeAccess } from "@/types";

// Two kinds of selection share this store. A label selection (org + label)
// drives the label-side dashboard. A payee selection puts the app in portal
// mode: currentLabel is the payee's label, currentOrganization is null.
//
// currentLabel stays set in portal mode so LabelBoundary's reset logic keeps
// working. Label-scoped API hooks would fetch label-side data off it and get
// 403s; portal pages must use portal hooks, and shared components that read
// currentLabel must check currentPayee first.
type OrganizationState = {
  currentOrganization: Organization | null;
  currentLabel: Label | null;
  currentPayee: PayeeAccess | null;
  setSelection: (org: Organization, label: Label) => void;
  setPayeeSelection: (payee: PayeeAccess) => void;
  clearSelection: () => void;
  initFromUser: (user: User | null | undefined) => void;
};

export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set, get) => ({
      currentOrganization: null,
      currentLabel: null,
      currentPayee: null,
      setSelection: (org, label) =>
        set({ currentOrganization: org, currentLabel: label, currentPayee: null }),
      setPayeeSelection: (payee) =>
        set({ currentOrganization: null, currentLabel: payee.label, currentPayee: payee }),
      clearSelection: () =>
        set({ currentOrganization: null, currentLabel: null, currentPayee: null }),
      initFromUser: (user) => {
        if (!user) return;
        const { currentOrganization, currentLabel, currentPayee } = get();

        // Reconcile the persisted selection with what the user can access now.
        // Access may have been revoked or the objects renamed since last session.
        if (currentPayee) {
          const fresh = user.payees?.find((p) => p.id === currentPayee.id);
          if (fresh) return get().setPayeeSelection(fresh);
        } else if (currentLabel) {
          const org = user.organizations.find((o) => o.id === currentOrganization?.id);
          const fresh = org?.labels.find((l) => l.id === currentLabel.id);
          if (org && fresh) return get().setSelection(org, fresh);
        }

        const orgsWithLabels = user.organizations.filter((o) => o.labels?.length);
        if (orgsWithLabels.length)
          return get().setSelection(orgsWithLabels[0], orgsWithLabels[0].labels[0]);
        if (user.payees?.length) return get().setPayeeSelection(user.payees[0]);
        get().clearSelection();
      },
    }),
    { name: "organization-selection" },
  ),
);
